import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Images,
  Plus,
  Upload,
  Star,
  Trash2,
  Pencil,
  X,
  FileText,
  Video,
  Loader2,
  AlertCircle,
  Eye,
  Lock,
  Users2,
} from "lucide-react";
import {
  CATEGORIAS_PORTAFOLIO_FALLBACK,
  LIMITE_ADJUNTOS_POR_ITEM,
  LIMITE_TAMANO_MB,
  actualizarItem,
  crearItem,
  eliminarAdjunto,
  eliminarItem,
  fetchCategorias,
  fetchPortafolio,
  subirAdjunto,
} from "../../lib/portafolio";
import { humanizeAxiosError } from "../../lib/api";
import AuthedImage from "../../components/AuthedImage";
import { useProfile } from "../../context/ProfileContext";

/* -------------------------------------------------------------------------- */
/*  UI helpers                                                                */
/* -------------------------------------------------------------------------- */

const VISIBILIDAD_META = {
  publica: { label: "Pública", icon: Eye, tone: "text-emerald-700 bg-emerald-50" },
  conexiones: { label: "Conexiones", icon: Users2, tone: "text-sky-700 bg-sky-50" },
  privada: { label: "Privada", icon: Lock, tone: "text-slate-600 bg-slate-100" },
};

function VisibilityBadge({ value }) {
  const meta = VISIBILIDAD_META[value] || VISIBILIDAD_META.publica;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${meta.tone}`}
    >
      <Icon size={11} />
      {meta.label}
    </span>
  );
}

function EmptyState({ onCrear }) {
  return (
    <div
      className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center"
      data-testid="portafolio-empty"
    >
      <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-blue/10 text-brand-navy flex items-center justify-center">
        <Images size={28} />
      </div>
      <h2 className="mt-5 text-xl font-bold text-slate-900">Tu vitrina está vacía</h2>
      <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
        Muestra tus mejores trabajos, fotos, videos y certificados. Lo que
        publiques como <strong>público</strong> aparecerá en tu perfil de
        ConstruLink Red. Tú controlas qué es público o privado.
      </p>
      <button
        type="button"
        onClick={onCrear}
        className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-red text-white text-sm font-bold hover:bg-red-700 transition-colors shadow-sm"
        data-testid="portafolio-empty-crear"
      >
        <Plus size={16} /> Añadir mi primer elemento
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal de creación / edición                                               */
/* -------------------------------------------------------------------------- */

function ItemFormModal({ open, initial, categorias, onClose, onSaved }) {
  const initialForm = {
    titulo: "",
    descripcion: "",
    categoria: "fotografia",
    anio: "",
    cliente: "",
    ubicacion_texto: "",
    video_url: "",
    destacado: false,
    visibilidad: "publica",
  };
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]); // File[] a subir (fotos/certificados)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        titulo: initial?.titulo || "",
        descripcion: initial?.descripcion || "",
        categoria: initial?.categoria || "fotografia",
        anio: initial?.anio ? String(initial.anio) : "",
        cliente: initial?.cliente || "",
        ubicacion_texto: initial?.ubicacion_texto || "",
        video_url: initial?.video_url || "",
        destacado: !!initial?.destacado,
        visibilidad: initial?.visibilidad === "privada" ? "privada" : "publica",
      });
      setFiles([]);
      setError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const isEdit = !!initial?.id;
  const esVideo = form.categoria === "video";

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.titulo.trim().length < 2) {
      setError("El título debe tener al menos 2 caracteres");
      return;
    }
    if (esVideo && !form.video_url.trim()) {
      setError("Ingresa la URL del video (YouTube, Vimeo, etc.)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || null,
        categoria: form.categoria,
        anio: form.anio ? parseInt(form.anio, 10) : null,
        cliente: form.cliente.trim() || null,
        ubicacion_texto: form.ubicacion_texto.trim() || null,
        video_url: esVideo ? form.video_url.trim() : null,
        destacado: !!form.destacado,
        visibilidad: form.visibilidad,
      };
      const result = isEdit
        ? await actualizarItem(initial.id, payload)
        : await crearItem(payload);
      // Subir archivos seleccionados (fotos/certificados) al item recién creado/editado.
      if (!esVideo && files.length > 0) {
        for (const f of files) {
          await subirAdjunto(result.id, f);
        }
      }
      onSaved(result, !isEdit);
    } catch (err) {
      setError(humanizeAxiosError(err));
    } finally {
      setSaving(false);
    }
  };

  const acceptFiles =
    form.categoria === "certificado"
      ? "image/jpeg,image/png,image/webp,application/pdf"
      : "image/jpeg,image/png,image/webp,image/gif";

  const pickFiles = (list) => {
    const arr = Array.from(list || []);
    const validos = arr.filter((f) => f.size <= LIMITE_TAMANO_MB * 1024 * 1024);
    if (validos.length !== arr.length) {
      setError(`Algunos archivos superan ${LIMITE_TAMANO_MB} MB y se omitieron`);
    }
    setFiles((prev) => [...prev, ...validos].slice(0, LIMITE_ADJUNTOS_POR_ITEM));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="portafolio-modal"
    >
      <form
        onSubmit={handleSave}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">
            {isEdit ? "Editar elemento" : "Añadir a la vitrina"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
            data-testid="portafolio-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
              Tipo de elemento
            </label>
            <select
              value={form.categoria}
              onChange={(e) => { setForm({ ...form, categoria: e.target.value }); setFiles([]); }}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              data-testid="portafolio-form-categoria"
            >
              {categorias.map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
              Título *
            </label>
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              maxLength={120}
              placeholder="Ej. Vivienda unifamiliar Miraflores"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              data-testid="portafolio-form-titulo"
              required
            />
          </div>

          {/* Campo reactivo: Video → URL · Foto/Certificado → uploader */}
          {esVideo ? (
            <div data-testid="portafolio-form-video-block">
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                URL del video *
              </label>
              <input
                type="url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=…"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                data-testid="portafolio-form-video-url"
              />
              <p className="mt-1 text-[11px] text-slate-400">Pega el enlace de YouTube, Vimeo o similar.</p>
            </div>
          ) : (
            <div data-testid="portafolio-form-file-block">
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                {form.categoria === "certificado" ? "Certificado (imagen o PDF)" : "Fotografías"}
              </label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-blue/60 hover:bg-slate-50"
                data-testid="portafolio-form-file-btn"
              >
                <Upload size={16} />
                {files.length > 0 ? `${files.length} archivo(s) seleccionado(s)` : "Seleccionar archivos"}
              </button>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept={acceptFiles}
                className="hidden"
                onChange={(e) => pickFiles(e.target.files)}
                data-testid="portafolio-form-file-input"
              />
              {files.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-xs text-slate-600 bg-slate-50 rounded-md px-2 py-1">
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                        className="text-slate-400 hover:text-rose-600 ml-2"
                        aria-label="Quitar"
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-[11px] text-slate-400">Máx {LIMITE_TAMANO_MB} MB por archivo · hasta {LIMITE_ADJUNTOS_POR_ITEM}.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Año
              </label>
              <input
                type="number"
                min="1950"
                max="2100"
                value={form.anio}
                onChange={(e) => setForm({ ...form, anio: e.target.value })}
                placeholder="2024"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                data-testid="portafolio-form-anio"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Cliente / Proyecto
              </label>
              <input
                type="text"
                value={form.cliente}
                onChange={(e) => setForm({ ...form, cliente: e.target.value })}
                maxLength={120}
                placeholder="Familia López"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                data-testid="portafolio-form-cliente"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
              Ubicación
            </label>
            <input
              type="text"
              value={form.ubicacion_texto}
              onChange={(e) => setForm({ ...form, ubicacion_texto: e.target.value })}
              maxLength={200}
              placeholder="Miraflores, Lima"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              data-testid="portafolio-form-ubicacion"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
              Descripción
            </label>
            <textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              rows={3}
              maxLength={2000}
              placeholder="Cuenta detalles del proyecto: alcance, materiales, plazos…"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
              data-testid="portafolio-form-descripcion"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {form.descripcion.length}/2000
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                Visibilidad
              </label>
              <select
                value={form.visibilidad}
                onChange={(e) => setForm({ ...form, visibilidad: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                data-testid="portafolio-form-visibilidad"
              >
                <option value="publica">Público (visible en tu perfil de la Red)</option>
                <option value="privada">Privado (solo tú)</option>
              </select>
            </div>
            <div className="self-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.destacado}
                  onChange={(e) => setForm({ ...form, destacado: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                  data-testid="portafolio-form-destacado"
                />
                <span className="text-sm text-slate-700 font-medium">Destacar</span>
              </label>
              <p className="text-[10px] text-brand-navy/60 mt-0.5">Beneficio Plan Elite (próximamente)</p>
            </div>
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
            data-testid="portafolio-form-cancelar"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-brand-red text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="portafolio-form-guardar"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? "Guardar cambios" : "Añadir a la vitrina"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dropzone de adjuntos por obra                                             */
/* -------------------------------------------------------------------------- */

function AdjuntosPanel({ item, onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState([]); // [{name, pct}]
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(false);

  const puedeSubirMas = item.adjuntos.length < LIMITE_ADJUNTOS_POR_ITEM;

  const validarArchivo = useCallback((f) => {
    if (f.size > LIMITE_TAMANO_MB * 1024 * 1024) {
      return `“${f.name}” supera ${LIMITE_TAMANO_MB} MB`;
    }
    const okTypes = [
      "image/jpeg", "image/png", "image/webp", "image/gif",
      "application/pdf",
      "video/mp4", "video/webm", "video/quicktime",
    ];
    if (!okTypes.includes(f.type)) {
      return `“${f.name}” no es imagen, PDF ni video`;
    }
    return null;
  }, []);

  const handleFiles = useCallback(
    async (files) => {
      setError(null);
      const validos = [];
      for (const f of files) {
        const err = validarArchivo(f);
        if (err) {
          setError(err);
          continue;
        }
        validos.push(f);
      }
      const disponibles = LIMITE_ADJUNTOS_POR_ITEM - item.adjuntos.length;
      const aSubir = validos.slice(0, disponibles);
      if (validos.length > disponibles) {
        setError(`Solo puedes subir ${disponibles} archivo(s) más (máximo ${LIMITE_ADJUNTOS_POR_ITEM}).`);
      }
      setUploading(aSubir.map((f) => ({ name: f.name, pct: 0 })));
      for (let i = 0; i < aSubir.length; i++) {
        const f = aSubir[i];
        try {
          await subirAdjunto(item.id, f, (pct) => {
            setUploading((prev) =>
              prev.map((u, idx) => (idx === i ? { ...u, pct } : u))
            );
          });
        } catch (err) {
          setError(humanizeAxiosError(err));
          break;
        }
      }
      setUploading([]);
      onChanged();
    },
    [item, onChanged, validarArchivo]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.length) handleFiles(Array.from(e.dataTransfer.files));
  };

  const onClickBorrar = async (adjId) => {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    try {
      await eliminarAdjunto(adjId);
      onChanged();
    } catch (err) {
      setError(humanizeAxiosError(err));
    }
  };

  return (
    <div className="space-y-3">
      {puedeSubirMas && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
            drag ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 hover:border-brand-blue/60 hover:bg-slate-50"
          }`}
          data-testid={`portafolio-dropzone-${item.id}`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => e.target.files && handleFiles(Array.from(e.target.files))}
            data-testid={`portafolio-file-input-${item.id}`}
          />
          <Upload size={18} className="mx-auto text-slate-400" />
          <p className="mt-1.5 text-xs text-slate-600 font-medium">
            Arrastra fotos, videos o PDFs aquí, o haz clic para seleccionar
          </p>
          <p className="text-[11px] text-slate-400">
            {item.adjuntos.length}/{LIMITE_ADJUNTOS_POR_ITEM} archivos · Máx {LIMITE_TAMANO_MB} MB
          </p>
        </div>
      )}

      {uploading.length > 0 && (
        <div className="space-y-1.5">
          {uploading.map((u, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
              <Loader2 size={12} className="animate-spin text-brand-blue" />
              <span className="flex-1 truncate">{u.name}</span>
              <span className="text-slate-400">{u.pct}%</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-100 rounded-lg text-xs text-red-800">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {item.adjuntos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {item.adjuntos.map((adj) => (
            <div
              key={adj.id}
              className="group relative aspect-square rounded-lg overflow-hidden border border-slate-100 bg-slate-50"
              data-testid={`portafolio-adjunto-${adj.id}`}
            >
              {adj.tipo === "imagen" ? (
                <AuthedImage
                  url={adj.url}
                  alt={adj.nombre_original}
                  className="w-full h-full object-cover"
                  testid={`portafolio-adjunto-img-${adj.id}`}
                />
              ) : adj.tipo === "video" ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 p-2 bg-slate-900/5">
                  <Video size={22} />
                  <p className="mt-1 text-[10px] text-center line-clamp-2 leading-tight">
                    {adj.nombre_original}
                  </p>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 p-2">
                  <FileText size={22} />
                  <p className="mt-1 text-[10px] text-center line-clamp-2 leading-tight">
                    {adj.nombre_original}
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={() => onClickBorrar(adj.id)}
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md bg-black/60 text-white"
                aria-label="Eliminar archivo"
                data-testid={`portafolio-adjunto-eliminar-${adj.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Card de item                                                              */
/* -------------------------------------------------------------------------- */

function PortafolioCard({ item, categorias, onEdit, onDelete, onChanged }) {
  const catLabel = useMemo(
    () => categorias.find((c) => c.codigo === item.categoria)?.etiqueta || item.categoria,
    [item.categoria, categorias]
  );

  return (
    <article
      className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`portafolio-item-${item.id}`}
    >
      <div className="aspect-video bg-slate-100 relative">
        {item.portada_url ? (
          <AuthedImage
            url={item.portada_url}
            alt={item.titulo}
            className="w-full h-full object-cover"
            testid={`portafolio-item-portada-${item.id}`}
            fallback={<div className="w-full h-full flex items-center justify-center text-slate-400"><Images size={32} /></div>}
          />
        ) : item.video_url ? (
          <a
            href={item.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-slate-500 bg-slate-900/5 hover:bg-slate-900/10 transition-colors"
            data-testid={`portafolio-item-video-${item.id}`}
          >
            <Video size={30} className="text-brand-red" />
            <span className="text-xs font-semibold">Ver video</span>
          </a>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <Images size={32} />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {item.destacado && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-red text-white text-[10px] font-bold px-2 py-0.5">
              <Star size={11} /> Destacado
            </span>
          )}
          <VisibilityBadge value={item.visibilidad} />
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3
              className="text-base font-bold text-slate-900 truncate"
              data-testid={`portafolio-item-titulo-${item.id}`}
            >
              {item.titulo}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {catLabel}
              {item.anio && ` · ${item.anio}`}
              {item.ubicacion_texto && ` · ${item.ubicacion_texto}`}
            </p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => onEdit(item)}
              className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
              aria-label="Editar"
              data-testid={`portafolio-editar-${item.id}`}
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(item)}
              className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"
              aria-label="Eliminar"
              data-testid={`portafolio-eliminar-${item.id}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        {item.descripcion && (
          <p className="text-sm text-slate-600 line-clamp-2">{item.descripcion}</p>
        )}
        {item.categoria !== "video" && <AdjuntosPanel item={item} onChanged={onChanged} />}
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*  Página principal                                                          */
/* -------------------------------------------------------------------------- */

export default function PortafolioPage() {
  const { refreshProgreso } = useProfile();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limite, setLimite] = useState(30);
  const [categorias, setCategorias] = useState(CATEGORIAS_PORTAFOLIO_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const data = await fetchPortafolio();
      setItems(data.items);
      setTotal(data.total);
      setLimite(data.limite_por_usuario || 30);
    } catch (e) {
      setError(humanizeAxiosError(e));
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cats] = await Promise.all([fetchCategorias().catch(() => null), cargar()]);
        if (cats) setCategorias(cats);
      } finally {
        setLoading(false);
      }
    })();
  }, [cargar]);

  const handleSaved = async (item, isNew) => {
    setModal({ open: false, initial: null });
    await cargar();
    if (isNew) await refreshProgreso();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar “${item.titulo}” y sus archivos?`)) return;
    try {
      await eliminarItem(item.id);
      await cargar();
      await refreshProgreso();
    } catch (e) {
      setError(humanizeAxiosError(e));
    }
  };
  const handleAdjuntoChanged = async () => {
    await cargar();
    await refreshProgreso();
  };

  if (loading) {
    return (
      <div
        className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-500"
        data-testid="portafolio-loading"
      >
        <Loader2 size={22} className="mx-auto animate-spin" />
        <p className="mt-2 text-sm">Cargando portafolio…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="portafolio-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Images size={20} className="text-brand-navy" />
            Portafolio
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Tu vitrina pública · {total} de {limite} elementos.
          </p>
        </div>
        {total < limite && (
          <button
            type="button"
            onClick={() => setModal({ open: true, initial: null })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-red text-white text-sm font-bold hover:bg-red-700 shadow-sm"
            data-testid="portafolio-nueva"
          >
            <Plus size={16} /> Añadir elemento
          </button>
        )}
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {total === 0 ? (
        <EmptyState onCrear={() => setModal({ open: true, initial: null })} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((item) => (
            <PortafolioCard
              key={item.id}
              item={item}
              categorias={categorias}
              onEdit={(it) => setModal({ open: true, initial: it })}
              onDelete={handleDelete}
              onChanged={handleAdjuntoChanged}
            />
          ))}
        </div>
      )}

      <ItemFormModal
        open={modal.open}
        initial={modal.initial}
        categorias={categorias}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={handleSaved}
      />
    </div>
  );
}
